import React from 'react';
import { useProCalculator } from '../hooks/useProCalculator';
import SectionHelp from './SectionHelp';
import * as XLSX from 'xlsx';
import '../styles/ResultsSection.css';
import AmazonLinksSection from './sections/AmazonLinksSection';

const ResultsSection = () => {
  const {
    cookieRows,
    dimensionsRows,
    colorRows,
    colorNames,
    orderRows,
    useSets,
    setRows,
    isMetricWeight,
    calculateColorIcing,
    getColorStyle,
    copySuccess,
    setCopySuccess,
    exportSuccess,
    setExportSuccess,
    isMobile,
    sectionCompletionStatus
  } = useProCalculator();

  let shouldBlurResults = false;
  if (sectionCompletionStatus) {
    if (
      sectionCompletionStatus.reference === 'incomplete' ||
      sectionCompletionStatus.cookieNames === 'incomplete' ||
      sectionCompletionStatus.dimensions === 'incomplete' ||
      sectionCompletionStatus.colors === 'incomplete' ||
      (useSets && sectionCompletionStatus.sets === 'incomplete') ||
      (!useSets && sectionCompletionStatus.orders === 'incomplete')
    ) {
      shouldBlurResults = true;
    }
  }

  // Function to copy results to clipboard
  const handleCopyResults = () => {
    // Create a string with the results
    let resultsText = 'Cookie Icing Calculator Results\n\n';
    
    // Add date
    resultsText += `Date: ${new Date().toLocaleDateString()}\n\n`;
    
    // Add results table header
    if (useSets) {
      resultsText += 'Set Name\tCookie Name\t';
    } else {
      resultsText += 'Cookie Name\t';
    }
    
    // Add color names to header
    colorNames.forEach(color => {
      resultsText += `${color}\t`;
    });
    
    resultsText += 'Total\n';
    
    // Process the results
    const resultRows = [];
    const colorTotals = Array(colorNames.length).fill(0);
    let grandTotal = 0;
    const processedRows = new Set();
    
    if (useSets) {
      // Group set rows by set name
      const setGroups = {};
      setRows.forEach(setRow => {
        if (!setRow.cookieId || !setRow.setName) return;
        
        if (!setGroups[setRow.setName]) {
          setGroups[setRow.setName] = [];
        }
        setGroups[setRow.setName].push(setRow);
      });
      
      // Process each set group
      Object.entries(setGroups).forEach(([setName, rows]) => {
        rows.forEach(setRow => {
          // Find the cookie
          const cookie = cookieRows.find(c => c.id === setRow.cookieId);
          if (!cookie) return;
          
          // Use the count from the set row
          const count = setRow.count;
          const colorAmounts = setRow.cookieId ? calculateColorIcing(setRow.cookieId, count) : Array(colorNames.length).fill(0);
          
          // Add to totals
          let rowTotal = 0;
          colorAmounts.forEach((amount, i) => {
            rowTotal += amount;
            colorTotals[i] += amount;
          });
          grandTotal += rowTotal;
          
          // Add to result rows
          resultRows.push({
            id: `${setRow.id}_${setRow.cookieId}`,
            setName,
            cookieName: cookie.cookieName,
            colorAmounts,
            total: rowTotal
          });
          
          processedRows.add(setRow.cookieId);
        });
      });
      
      // Add rows to results text
      resultRows.forEach(row => {
        resultsText += `${row.setName}\t${row.cookieName}\t`;
        row.colorAmounts.forEach(amount => {
          resultsText += `${amount.toFixed(2)} g\t`;
        });
        resultsText += `${row.total.toFixed(2)} g\n`;
      });
    } else {
      // Not using sets, just process each cookie
      cookieRows.forEach(cookie => {
        // Get the count from the order rows when not using Sets
        const orderRow = orderRows.find(o => o.cookieId === cookie.id);
        const count = orderRow ? (orderRow.count || 1) : 1;
        
        // Calculate color amounts for this cookie
        const colorAmounts = calculateColorIcing(cookie.id, count);
        
        // Add to totals
        let rowTotal = 0;
        colorAmounts.forEach((amount, i) => {
          rowTotal += amount;
          colorTotals[i] += amount;
        });
        grandTotal += rowTotal;
        
        // Add to result rows
        resultRows.push({
          id: cookie.id,
          cookieName: cookie.cookieName,
          colorAmounts,
          total: rowTotal
        });
        
        processedRows.add(cookie.id);
      });
      
      // Add rows to results text
      resultRows.forEach(row => {
        resultsText += `${row.cookieName}\t`;
        row.colorAmounts.forEach(amount => {
          resultsText += `${amount.toFixed(2)} g\t`;
        });
        resultsText += `${row.total.toFixed(2)} g\n`;
      });
    }
    
    // Add totals row
    resultsText += `Total\t`;
    if (useSets) {
      resultsText += `\t`;
    }
    
    colorTotals.forEach(total => {
      resultsText += `${total.toFixed(2)} g\t`;
    });
    
    resultsText += `${grandTotal.toFixed(2)} g\n\n`;
    
    // Add color summary
    resultsText += 'Color Summary:\n';
    colorNames.forEach((color, index) => {
      const totalGrams = colorTotals[index];  // This is in grams
      const totalOunces = totalGrams / 28.35; // Convert to ounces
      
      // Format: Grams g / Ounces oz
      resultsText += `${color}: ${totalGrams.toFixed(2)} g / ${totalOunces.toFixed(2)} oz\n`;
    });
    
    // Copy to clipboard
    navigator.clipboard.writeText(resultsText)
      .then(() => {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      })
      .catch(err => {
        console.error('Failed to copy results: ', err);
        alert('Failed to copy results to clipboard. Please try again.');
      });
  };

  // Function to export results to Excel
  const exportToExcel = () => {
    // Create a new workbook
    const wb = XLSX.utils.book_new();
    
    // Create data array for the worksheet
    const data = [];
    
    // Add title and date
    data.push(['Cookie Icing Calculator Results']);
    data.push([`Date: ${new Date().toLocaleDateString()}`]);
    data.push([]);
    
    // Add results table header
    const header = [];
    if (useSets) {
      header.push('Set Name', 'Cookie Name');
    } else {
      header.push('Cookie Name');
    }
    
    // Add color names to header
    colorNames.forEach(color => {
      header.push(color);
    });
    
    header.push('Total');
    data.push(header);
    
    // Process the results
    const resultRows = [];
    const colorTotals = Array(colorNames.length).fill(0);
    let grandTotal = 0;
    const processedRows = new Set();
    
    if (useSets) {
      // Group set rows by set name
      const setGroups = {};
      setRows.forEach(setRow => {
        if (!setRow.cookieId || !setRow.setName) return;
        
        if (!setGroups[setRow.setName]) {
          setGroups[setRow.setName] = [];
        }
        setGroups[setRow.setName].push(setRow);
      });
      
      // Process each set group
      Object.entries(setGroups).forEach(([setName, rows]) => {
        rows.forEach(setRow => {
          // Find the cookie
          const cookie = cookieRows.find(c => c.id === setRow.cookieId);
          if (!cookie) return;
          
          // Use the count from the set row
          const count = setRow.count;
          const colorAmounts = setRow.cookieId ? calculateColorIcing(setRow.cookieId, count) : Array(colorNames.length).fill(0);
          
          // Add to totals
          let rowTotal = 0;
          colorAmounts.forEach((amount, i) => {
            rowTotal += amount;
            colorTotals[i] += amount;
          });
          grandTotal += rowTotal;
          
          // Add to result rows
          resultRows.push({
            id: `${setRow.id}_${setRow.cookieId}`,
            setName,
            cookieName: cookie.cookieName,
            colorAmounts,
            total: rowTotal
          });
          
          processedRows.add(setRow.cookieId);
        });
      });
      
      // Add rows to Excel data
      resultRows.forEach(row => {
        const rowData = [row.setName, row.cookieName];
        row.colorAmounts.forEach(amount => {
          rowData.push(`${amount.toFixed(2)} g`);
        });
        rowData.push(`${row.total.toFixed(2)} g`);
        data.push(rowData);
      });
    } else {
      data.push(['RESULTS BY COOKIE']);
      data.push([]);
      
      // Not using sets, just process each cookie
      cookieRows.forEach(cookie => {
        // Get the count from the order rows when not using Sets
        const orderRow = orderRows.find(o => o.cookieId === cookie.id);
        const count = orderRow ? (orderRow.count || 1) : 1;
        
        // Calculate color amounts for this cookie
        const colorAmounts = calculateColorIcing(cookie.id, count);
        
        // Add to totals
        let rowTotal = 0;
        colorAmounts.forEach((amount, i) => {
          rowTotal += amount;
          colorTotals[i] += amount;
        });
        grandTotal += rowTotal;
        
        // Add to result rows
        resultRows.push({
          id: cookie.id,
          cookieName: cookie.cookieName,
          colorAmounts,
          total: rowTotal
        });
        
        processedRows.add(cookie.id);
      });
      
      // Add rows to Excel data
      resultRows.forEach(row => {
        const rowData = [row.cookieName];
        row.colorAmounts.forEach(amount => {
          rowData.push(`${amount.toFixed(2)} g`);
        });
        rowData.push(`${row.total.toFixed(2)} g`);
        data.push(rowData);
      });
    }
    
    // Add totals row
    const totalsRow = ['Total'];
    if (useSets) {
      totalsRow.push('');
    }
    
    colorTotals.forEach(total => {
      totalsRow.push(`${total.toFixed(2)} g`);
    });
    
    totalsRow.push(`${grandTotal.toFixed(2)} g`);
    data.push(totalsRow);
    
    data.push([]);
    data.push([]);
    
    // Add color summary
    data.push(['COLOR SUMMARY']);
    data.push([]);
    
    colorNames.forEach((color, index) => {
      const totalGrams = colorTotals[index]; // This is the total for the color in grams
      const totalOunces = totalGrams / 28.35; // Convert grams to ounces
      
      // Format: Color Name, Grams g, Ounces oz
      data.push([color, `${totalGrams.toFixed(2)} g`, `${totalOunces.toFixed(2)} oz`]);
    });
    
    // Create worksheet from data
    const ws = XLSX.utils.aoa_to_sheet(data);
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Icing Calculations');
    
    // Generate Excel file and trigger download
    const date = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `Icing_Calculator_Results_${date}.xlsx`);
    
    setExportSuccess(true);
    setTimeout(() => setExportSuccess(false), 2000);
  };

  return (
    <div className="results-section">
      <div className="section-header">
        <h2 className="section-title">Results</h2>
      </div>
      {/* Help text container added */}
      <div className="section-help-container">
        <SectionHelp sectionId="results" isMobile={isMobile} />
      </div>

      <div className={`results-content-wrapper ${shouldBlurResults ? 'blur-active' : ''}`}>
        {shouldBlurResults && (
          <div className="results-blur-overlay">
            Please complete all required sections to view results.
          </div>
        )}

        {/* Results Table */}
        <div className="results-table">
          <div className="results-table-header">
            {useSets && <div className="set-name-header">Set Name</div>}
            <div className="cookie-name-header">Cookie Name</div>
            
            {colorNames.map((color, index) => (
              <div key={index} className="color-amount-header">
                <div className="color-sample" style={getColorStyle(color)}></div>
                <span>{color}</span>
              </div>
            ))}
            
            <div className="total-header">Total (g)</div>
          </div>
          
          {(() => {
            // Process the results
            const resultRows = [];
            const colorTotals = Array(colorNames.length).fill(0);
            let grandTotal = 0;
            const processedRows = new Set();
            
            if (useSets) {
              // Group set rows by set name
              const setGroups = {};
              setRows.forEach(setRow => {
                if (!setRow.cookieId || !setRow.setName) return;
                
                if (!setGroups[setRow.setName]) {
                  setGroups[setRow.setName] = [];
                }
                setGroups[setRow.setName].push(setRow);
              });
              
              // Process each set group
              Object.entries(setGroups).forEach(([setName, rows]) => {
                rows.forEach(setRow => {
                  // Find the cookie
                  const cookie = cookieRows.find(c => c.id === setRow.cookieId);
                  if (!cookie) return;
                  
                  // Use the count from the set row
                  const count = setRow.count;
                  const colorAmounts = setRow.cookieId ? calculateColorIcing(setRow.cookieId, count) : Array(colorNames.length).fill(0);
                  
                  // Add to totals
                  let rowTotal = 0;
                  colorAmounts.forEach((amount, i) => {
                    rowTotal += amount;
                    colorTotals[i] += amount;
                  });
                  grandTotal += rowTotal;
                  
                  // Add to result rows
                  resultRows.push({
                    id: `${setRow.id}_${setRow.cookieId}`,
                    setName,
                    cookieName: cookie.cookieName,
                    colorAmounts,
                    total: rowTotal
                  });
                  
                  processedRows.add(setRow.cookieId);
                });
              });
            } else {
              // Not using sets, just process each cookie
              cookieRows.forEach(cookie => {
                // Get the count from the order rows when not using Sets
                const orderRow = orderRows.find(o => o.cookieId === cookie.id);
                const count = orderRow ? (orderRow.count || 1) : 1;
                
                // Calculate color amounts for this cookie
                const colorAmounts = calculateColorIcing(cookie.id, count);
                
                // Add to totals
                let rowTotal = 0;
                colorAmounts.forEach((amount, i) => {
                  rowTotal += amount;
                  colorTotals[i] += amount;
                });
                grandTotal += rowTotal;
                
                // Add to result rows
                resultRows.push({
                  id: cookie.id,
                  cookieName: cookie.cookieName,
                  colorAmounts,
                  total: rowTotal
                });
                
                processedRows.add(cookie.id);
              });
            }
            
            // Render the rows
            return (
              <>
                {resultRows.map(row => (
                  <div key={row.id} className="results-row">
                    {useSets && <div className="set-name-field">{row.setName}</div>}
                    <div className="cookie-name-field">{row.cookieName}</div>
                    
                    {row.colorAmounts.map((amount, index) => (
                      <div key={index} className="color-amount-field">
                        {amount.toFixed(2)} g
                      </div>
                    ))}
                    
                    <div className="total-field">
                      {row.total.toFixed(2)} g
                    </div>
                  </div>
                ))}
                
                {/* Totals row */}
                <div className="results-row totals-row">
                  <div className={`totals-label-field ${useSets ? 'with-sets' : ''}`}>
                    Total
                  </div>
                  
                  {colorTotals.map((total, index) => (
                    <div key={index} className="color-amount-field total-value">
                      {total.toFixed(2)} g
                    </div>
                  ))}
                  
                  <div className="total-field grand-total">
                    {grandTotal.toFixed(2)} g
                  </div>
                </div>
              </>
            );
          })()}
        </div>
        
        {/* Results action buttons */}
        <div className="button-container">
          <button 
            className={`copy-button ${copySuccess ? 'copied' : ''}`}
            onClick={handleCopyResults}
            title="Copy results to clipboard"
          >
            {copySuccess ? '✓ Copied!' : '📋 Copy Results'}
          </button>
          
          <button
            className={`excel-button ${exportSuccess ? 'exported' : ''}`}
            onClick={exportToExcel}
            title="Export results to Excel"
          >
            {exportSuccess ? '✓ Downloaded!' : '📊 Export to Excel'}
          </button>
        </div>
        
        {/* Color summary section */}
        <div className="color-summary">
          <h3>Color Summary</h3>
          <div className="color-summary-list">
            {colorNames.map((color, index) => {
              // Get the total for this color from the last calculation
              const colorTotal = (() => {
                let total = 0;
                
                if (useSets) {
                  setRows.forEach(setRow => {
                    if (!setRow.cookieId) return;
                    const count = setRow.count;
                    const colorAmounts = calculateColorIcing(setRow.cookieId, count);
                    total += colorAmounts[index] || 0;
                  });
                } else {
                  cookieRows.forEach(cookie => {
                    if (!cookie.id) return; // Skip default/empty
                    
                    // Find the corresponding order row to get the count
                    const orderRow = orderRows.find(o => o.cookieId === cookie.id);
                    const count = orderRow ? (orderRow.count || 1) : 1; // Use count from orderRow, default to 1
                    
                    // Calculate icing for this cookie with its specific count
                    const colorAmounts = calculateColorIcing(cookie.id, count); 
                    total += colorAmounts[index] || 0; // Add the amount for the current color
                  });
                }
                return total;
              })();
              
              // Correct unit calculation and display order
              const gramsValue = colorTotal; // This is already in grams
              const ouncesValue = gramsValue / 28.35; // Convert grams to ounces
              
              return (
                <div key={index} className="color-summary-item">
                  <div className="color-sample" style={getColorStyle(color)}></div>
                  <div className="color-summary-content">
                    <div className="color-name">{color}</div>
                    <div className="color-measurements">
                      {/* Display grams first */}
                      <span className="measurement">{gramsValue.toFixed(2)} g</span>
                      <span className="measurement-separator">|</span>
                      {/* Display correctly converted ounces second */}
                      <span className="measurement">{ouncesValue.toFixed(2)} oz</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div> {/* End of results-content-wrapper */}
      
      {/* Recommended Tools Section */}
      <AmazonLinksSection title="Recommended Decorating Tools" />
    </div>
  );
};

export default ResultsSection;
